// Iter-284 · Branch-specific colour themes.
//
// Each Cocoon location is assigned a distinct primary colour so
// operators moving between branches get an instant visual cue about
// where they are. The rule set below is intentionally KEYWORD-driven
// (case-insensitive `includes`) so the mapping still lands even when
// the branch is named "Cocoon Medical Spa · Legian" or "Cocoon
// Bahrain 02" — the display-name convention is set by the manager
// and can drift over time.
//
// Colours are stored in the same `H S% L%` HSL triplet the rest of
// the design system uses (see index.css) so we can inject them into
// the shared `--primary` / `--primary-foreground` / `--ring` CSS
// variables at the StaffShell wrapper — every downstream Tailwind
// utility (`bg-primary`, `text-primary-foreground`, `hover:bg-primary/90`,
// `focus-visible:ring-ring`, etc.) picks the new colour up
// automatically.
//
// Owner Safeguard: only branch-context pages read this theme. The
// Brand Workspace (Overview, Services, Series, Packages, Products,
// Customers, Staff Logins, Gift Card, Loyalty Rewards, Branches,
// Automated Notifications, Reports, General / Categories / Cancellation
// Reasons / Launch Reset) keeps the default sage palette so operators
// can tell the master view from a branch view at a glance.

// Sage · #728971 — matches the base `--primary` in index.css. Kept
// here so `resolveBranchTheme` can return a full triplet for every
// branch (including Canggu) rather than a `null` special case.
const SAGE = { primary: "118 10% 49%", primaryFg: "0 0% 100%", ring: "118 10% 49%" };

// Ordered rules — first hit wins. The Bahrain rules are ordered from
// MOST-SPECIFIC ("Bahrain 3") to LEAST-SPECIFIC ("Bahrain") so a
// plain "Cocoon Bahrain" without a numeric suffix still lands on the
// first Bahrain colour (Gold). "Italy" is matched separately because
// the user's brief lists it as its own branch alongside the Bahrain
// trio.
const BRANCH_THEME_RULES = [
  { match: /legian/i,               theme: { primary: "345 100% 25%", primaryFg: "0 0% 100%",  ring: "345 100% 25%" } }, // Burgundy · #800020
  { match: /ubud/i,                 theme: { primary: "195 53% 79%",  primaryFg: "220 15% 20%", ring: "195 53% 45%"  } }, // Light Blue · #ADD8E6 (dark fg for legibility)
  { match: /jimbaran/i,             theme: { primary: "43 90% 52%",   primaryFg: "35 50% 15%", ring: "40 85% 42%"   } }, // Gold · matches the Jimbaran butterfly logo (dark fg for legibility)
  { match: /italy/i,                theme: { primary: "180 100% 25%", primaryFg: "0 0% 100%",  ring: "180 100% 25%" } }, // Teal · #008080
  { match: /bahrain\s*0?3/i,        theme: { primary: "240 100% 25%", primaryFg: "0 0% 100%",  ring: "240 100% 25%" } }, // Dark Navy · #000080
  { match: /bahrain\s*0?2/i,        theme: { primary: "28 100% 86%",  primaryFg: "20 40% 25%", ring: "28 80% 50%"   } }, // Soft Orange · #FFDAB9 (dark fg for legibility)
  { match: /bahrain/i,              theme: { primary: "46 65% 52%",   primaryFg: "0 0% 100%",  ring: "46 65% 52%"   } }, // Gold · #D4AF37
  { match: /canggu/i,               theme: SAGE },                                                                        // Green · #728971 (default)
];

/**
 * Resolve the CSS-variable triplet for the given branch name.
 *
 * @param {string|null|undefined} branchName — free-form display name
 *   (e.g. `Cocoon Medical Spa · Legian`, `Cocoon Bahrain 02`).
 * @returns {{primary: string, primaryFg: string, ring: string}}
 *   HSL triplets ready to drop into an inline `style` prop.
 */
export function resolveBranchTheme(branchName) {
  if (!branchName || typeof branchName !== "string") return SAGE;
  for (const rule of BRANCH_THEME_RULES) {
    if (rule.match.test(branchName)) return rule.theme;
  }
  // No keyword hit (e.g. an Iter190 test branch) — leave the palette
  // untouched so the default sage renders.
  return SAGE;
}

/**
 * Build the inline `style` object that swaps the shared design-system
 * CSS variables. Returns `undefined` for non-branch contexts so the
 * Brand Workspace keeps its default sage palette (Owner Safeguard).
 *
 * @param {"branch"|"brand"} context
 * @param {string|null|undefined} branchName
 * @returns {React.CSSProperties|undefined}
 */
export function branchThemeStyle(context, branchName) {
  if (context !== "branch") return undefined;
  const t = resolveBranchTheme(branchName);
  return {
    "--primary": t.primary,
    "--primary-foreground": t.primaryFg,
    "--ring": t.ring,
  };
}

// Exposed for the pytest regression guard so the mapping stays
// stable across future refactors.
export const _BRANCH_THEME_RULES = BRANCH_THEME_RULES;
export const _SAGE = SAGE;
